import {createContext, useContext, ReactNode, ChangeEvent} from 'react';

interface CheckboxContextType<T> {
  selectedValue: T | T[];
  onChangeCheckValue: (value: T) => void;
  multiple: boolean;
}

const CheckboxContext = createContext<CheckboxContextType<any> | undefined>(
  undefined,
);

interface CheckboxGroupProps<T> {
  children: ReactNode;
  title?: string | ReactNode;
  selectedValue: T | T[];
  onChangeSelectedValue: (values: T) => void;
  multiple?: boolean;
}

export function CheckboxGroup<T>({
  children,
  title,
  selectedValue,
  onChangeSelectedValue,
  multiple = false,
}: CheckboxGroupProps<T>) {
  const onChangeCheckValue = (value: T) => {
    onChangeSelectedValue(value);
  };

  return (
    <CheckboxContext.Provider
      value={{selectedValue, onChangeCheckValue, multiple}}>
      <div className='flex flex-col gap-4'>
        {title && <span className='text-sm'>{title}</span>}
        <div className='flex items-center gap-[1.625rem]'>{children}</div>
      </div>
    </CheckboxContext.Provider>
  );
}

interface CheckboxItemProps<T> {
  value: T;
  label: string;
}

export function CheckboxItem<T>({value, label}: CheckboxItemProps<T>) {
  const context = useContext(CheckboxContext);
  if (!context) {
    throw new Error('CheckboxItem must be used within a CheckboxGroup');
  }

  const {selectedValue, onChangeCheckValue, multiple} = context;

  const isChecked = multiple
    ? selectedValue.includes(value)
    : selectedValue === value;

  const handleChange = () => {
    onChangeCheckValue(value);
  };

  return (
    <label className='relative flex items-center gap-1.5 text-black cursor-pointer'>
      <input
        className='hidden'
        type='checkbox'
        id={String(value)}
        checked={isChecked}
        onChange={handleChange}
        value={String(value)}
      />
      <CheckboxIcon isCheck={isChecked} />
      {label}
    </label>
  );
}
type CheckboxIconProps = {
  isCheck: boolean;
} & React.SVGProps<SVGSVGElement>;

export function CheckboxIcon({isCheck, ...props}: CheckboxIconProps) {
  return isCheck ? (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      {...props}>
      <rect
        x='0.5'
        y='0.5'
        width='19'
        height='19'
        rx='5.5'
        fill='#800ADF'
        stroke='#800ADF'
      />
      <path
        d='M6 10.1907L8.55119 13L14 7'
        stroke='white'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ) : (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      {...props}>
      <rect
        x='0.5'
        y='0.5'
        width='19'
        height='19'
        rx='5.5'
        fill='white'
        stroke='#CFCFCF'
      />
      <path
        d='M6 10.1907L8.55119 13L14 7'
        stroke='#CFCFCF'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
